import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { COLORS } from '../theme';

const LANG_LABELS = {
  en: 'English',
  es: 'Spanish',
  zh: 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
  ko: 'Korean',
  ja: 'Japanese',
};

const LANG_CODES = ['en', 'es', 'zh', 'zh-TW', 'ko', 'ja'];

function nextQuestionId(existing) {
  // Existing ids are NOT guaranteed contiguous with existing.length — a prior
  // deletion leaves a gap (e.g. q6 deleted from q1..q9 leaves ids q1-q5,q7-q9,
  // still 8 questions long but max suffix 9), so deriving the new id from
  // existing.length + 1 can collide with an id that's still in use. Deriving
  // it from the max existing numeric suffix instead makes collision
  // impossible regardless of past deletions/reorders.
  const maxNum = existing.reduce((max, q) => {
    const match = /^q(\d+)$/.exec(q.id || '');
    return match ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);
  return `q${maxNum + 1}`;
}

function emptyQuestion(id, order) {
  return {
    id,
    order,
    required: true,
    text: { en: '', es: '', zh: '', 'zh-TW': '', ko: '', ja: '' },
  };
}

// Prompt #281 (HC-GUEST-SHOWCASE-INTRO): parameterized to also back the
// showcase-questions admin UI (config/guestShowcaseQuestions) — the only
// two things that differed between "onboarding questions" and "showcase
// questions" were the Firestore doc path (hardcoded at exactly 2 call
// sites: fetchQuestions/saveToFirestore below) and a header line naming
// which question set this page is editing. Everything else (CRUD,
// reorder, per-language textarea editor, auto-translate-on-save via the
// doc's own Firestore trigger) is identical for both.
export default function OnboardingQuestionsPage({
  docPath = 'onboardingQuestions',
  title = 'Onboarding Questions',
}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [isNewQuestion, setIsNewQuestion] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docPath]);

  async function fetchQuestions() {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'config', docPath));
      const raw = snap.exists() ? (snap.data().questions || []) : [];
      const sorted = [...raw].sort((a, b) => (a.order || 0) - (b.order || 0));
      setQuestions(sorted);
    } catch (e) {
      setError('Failed to load questions: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveToFirestore(updatedQuestions) {
    setSaving(true);
    setError(null);
    try {
      // Re-number orders to be sequential
      const reordered = updatedQuestions.map((q, i) => ({ ...q, order: i + 1 }));
      await setDoc(doc(db, 'config', docPath), { questions: reordered });
      setQuestions(reordered);
      setSuccessMsg('Saved successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e) {
      setError('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  function startEdit(q) {
    setEditingId(q.id);
    // PROMPT #295: `required` defaults to true for any question saved
    // before this field existed — same backward-compatible default the
    // mobile client and onboarding/showcase Cloud Functions use.
    setEditDraft({ ...q, required: q.required ?? true, text: { ...q.text } });
    setIsNewQuestion(false);
  }

  function setDraftRequired(value) {
    setEditDraft(prev => ({ ...prev, required: value }));
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(null);
    setIsNewQuestion(false);
  }

  function setDraftText(lang, value) {
    setEditDraft(prev => ({
      ...prev,
      text: { ...prev.text, [lang]: value },
    }));
  }

  async function saveEdit() {
    const updated = questions.map(q => (q.id === editingId ? editDraft : q));
    setEditingId(null);
    setEditDraft(null);
    setIsNewQuestion(false);
    await saveToFirestore(updated);
  }

  async function moveUp(index) {
    if (index === 0) return;
    const arr = [...questions];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    await saveToFirestore(arr);
  }

  async function moveDown(index) {
    if (index === questions.length - 1) return;
    const arr = [...questions];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    await saveToFirestore(arr);
  }

  async function addQuestion() {
    const newQ = emptyQuestion(nextQuestionId(questions), questions.length + 1);
    const updated = [...questions, newQ];
    setEditingId(newQ.id);
    setEditDraft({ ...newQ, text: { ...newQ.text } });
    setIsNewQuestion(true);
    setQuestions(updated);
  }

  async function deleteQuestion(id) {
    const updated = questions.filter(q => q.id !== id);
    setDeleteConfirmId(null);
    await saveToFirestore(updated);
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <span style={{ color: COLORS.textSecondary }}>Loading questions...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Page header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <div>
          <h2 style={{ color: COLORS.textPrimary, fontSize: '16px', fontWeight: '600', margin: '0 0 4px' }}>
            {title}
          </h2>
          <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>
            {questions.length} question{questions.length !== 1 ? 's' : ''} · 6 languages
          </p>
        </div>
        <button
          onClick={addQuestion}
          disabled={saving}
          style={{
            backgroundColor: COLORS.accent,
            color: COLORS.bg,
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          + Add Question
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#3b1515',
          border: `1px solid ${COLORS.danger}`,
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: COLORS.danger,
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {successMsg && (
        <div style={{
          backgroundColor: '#153b1e',
          border: `1px solid ${COLORS.success}`,
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: COLORS.success,
          fontSize: '13px',
        }}>
          {successMsg}
        </div>
      )}

      {/* Questions list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {questions.map((q, index) => (
          <div
            key={q.id}
            style={{
              backgroundColor: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            {editingId === q.id ? (
              // ── Edit mode ─────────────────────────────────────────────────
              <div>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: COLORS.accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                }}>
                  Question {index + 1} — Editing
                </div>
                {/* PROMPT #295: required/optional toggle — same shared
                    component backs both Onboarding Questions and Guest
                    Preview Questions (docPath prop), so this applies to
                    both automatically. */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: COLORS.textPrimary,
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={editDraft?.required ?? true}
                    onChange={e => setDraftRequired(e.target.checked)}
                  />
                  Required — worker/guest must answer before continuing
                </label>
                {(isNewQuestion ? ['en'] : LANG_CODES).map(lang => (
                  <div key={lang} style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      color: COLORS.textSecondary,
                      fontWeight: '600',
                      marginBottom: '4px',
                    }}>
                      {LANG_LABELS[lang]}
                    </label>
                    <textarea
                      value={editDraft?.text?.[lang] || ''}
                      onChange={e => setDraftText(lang, e.target.value)}
                      rows={2}
                      style={{
                        width: '100%',
                        backgroundColor: COLORS.bg,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '4px',
                        padding: '8px 10px',
                        color: COLORS.textPrimary,
                        fontSize: '13px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                      }}
                    />
                    {isNewQuestion && lang === 'en' && (
                      <p style={{
                        margin: '6px 0 0',
                        fontSize: '11px',
                        color: COLORS.textSecondary,
                        fontStyle: 'italic',
                      }}>
                        Other languages will be automatically translated after saving.
                      </p>
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    style={{
                      backgroundColor: COLORS.success,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 14px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={cancelEdit}
                    style={{
                      backgroundColor: 'transparent',
                      color: COLORS.textSecondary,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '4px',
                      padding: '6px 14px',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // ── View mode ──────────────────────────────────────────────────
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}>
                  {/* Order badge + English text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{
                        fontSize: '11px',
                        color: COLORS.accent,
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        Q{index + 1}
                      </div>
                      {/* PROMPT #295: required/optional badge */}
                      <span style={{
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        border: `1px solid ${COLORS.border}`,
                        color: (q.required ?? true) ? COLORS.textSecondary : COLORS.accent,
                        backgroundColor: COLORS.bg,
                      }}>
                        {(q.required ?? true) ? 'Required' : 'Optional'}
                      </span>
                    </div>
                    <div style={{
                      color: COLORS.textPrimary,
                      fontSize: '14px',
                      lineHeight: '1.5',
                      fontWeight: '500',
                    }}>
                      {q.text?.en || <span style={{ color: COLORS.textMuted }}>(no English text)</span>}
                    </div>
                    {/* Language previews */}
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {LANG_CODES.filter(l => l !== 'en').map(lang => (
                        <span
                          key={lang}
                          style={{
                            fontSize: '11px',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            backgroundColor: q.text?.[lang]
                              ? COLORS.bg
                              : '#3b1515',
                            color: q.text?.[lang]
                              ? COLORS.textSecondary
                              : COLORS.danger,
                            border: `1px solid ${COLORS.border}`,
                          }}
                        >
                          {lang}{!q.text?.[lang] && ' ✗'}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {/* Reorder */}
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0 || saving}
                      title="Move up"
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '4px',
                        color: index === 0 ? COLORS.textMuted : COLORS.textSecondary,
                        padding: '4px 8px',
                        cursor: index === 0 ? 'default' : 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === questions.length - 1 || saving}
                      title="Move down"
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '4px',
                        color: index === questions.length - 1 ? COLORS.textMuted : COLORS.textSecondary,
                        padding: '4px 8px',
                        cursor: index === questions.length - 1 ? 'default' : 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      ↓
                    </button>
                    {/* Edit */}
                    <button
                      onClick={() => startEdit(q)}
                      style={{
                        backgroundColor: COLORS.bg,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '4px',
                        color: COLORS.textPrimary,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Edit
                    </button>
                    {/* Delete */}
                    {deleteConfirmId === q.id ? (
                      <>
                        <button
                          onClick={() => deleteQuestion(q.id)}
                          style={{
                            backgroundColor: COLORS.danger,
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '4px',
                            color: COLORS.textSecondary,
                            padding: '4px 10px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(q.id)}
                        style={{
                          backgroundColor: 'transparent',
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: '4px',
                          color: COLORS.danger,
                          padding: '4px 10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {questions.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '48px',
          color: COLORS.textSecondary,
          fontSize: '14px',
        }}>
          No questions yet. Click &quot;+ Add Question&quot; to get started.
        </div>
      )}
    </div>
  );
}
